import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { HugeiconsIcon } from '@hugeicons/react'
import { AnalyticsUpIcon } from '@hugeicons/core-free-icons'

import { ErrorDisplay } from '@/components/common/error-display'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'

export interface ChartDataPoint {
  date: string
  displayDate: string
  managerScore: number | null
  directReportScore: number | null
}

export interface PositionProgressChartProps {
  data: Array<ChartDataPoint>
  isLoading: boolean
  error?: Error | null
}

const chartConfig = {
  managerScore: {
    label: 'Manager',
    color: 'var(--chart-1)',
  },
  directReportScore: {
    label: 'Direct Report',
    color: 'var(--chart-2)',
  },
}

export function PositionProgressChart({
  data,
  isLoading,
  error,
}: PositionProgressChartProps) {
  if (isLoading) {
    return (
      <Card className="h-[400px] flex flex-col p-6 bg-muted/30 rounded-lg border-none shadow-none">
        <CardHeader className="p-0 mb-4">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="flex-1 p-0">
          <Skeleton className="h-full w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        title="Failed to load position progress"
        variant="card"
      />
    )
  }

  if (data.length === 0) {
    return (
      <Card className="h-[400px] flex flex-col p-6 bg-muted/30 rounded-lg border-none shadow-none">
        <CardHeader className="p-0 mb-4">
          <CardTitle className="text-lg font-semibold">
            Position Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 p-0">
          <div className="flex h-full items-center justify-center flex-col gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <HugeiconsIcon
                icon={AnalyticsUpIcon}
                className="h-6 w-6 text-primary"
              />
            </div>
            <span className="text-muted-foreground">
              No assessment data available
            </span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-[400px] flex flex-col p-6 bg-muted/30 rounded-lg border-none shadow-none">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="text-lg font-semibold">
          Position Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 p-0 flex flex-col min-h-0">
        {/* Legend with colored labels */}
        <div className="flex gap-6 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-chart-1" />
            <span className="text-sm text-muted-foreground">
              Manager eval %
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-chart-2" />
            <span className="text-sm text-muted-foreground">
              Personal eval %
            </span>
          </div>
        </div>

        <ChartContainer config={chartConfig} className="flex-1 min-h-0 w-full">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="displayDate"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              domain={[0, 100]}
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => `${value.toFixed(0)}`}
            />
            <ChartTooltip
              cursor={false}
              contentStyle={{
                backgroundColor: 'var(--background)',
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
              formatter={(value) => `${Number(value).toFixed(2)}%`}
              labelFormatter={(label) => `${label}`}
            />
            {/* Manager line (Blue) */}
            <Line
              type="monotone"
              dataKey="managerScore"
              name="Manager"
              stroke="var(--color-managerScore)"
              strokeWidth={2}
              dot={{
                fill: 'var(--color-managerScore)',
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{
                r: 6,
              }}
              connectNulls
            />
            {/* Direct report line (Purple) */}
            <Line
              type="monotone"
              dataKey="directReportScore"
              name="Direct Report"
              stroke="var(--color-directReportScore)"
              strokeWidth={2}
              dot={{
                fill: 'var(--color-directReportScore)',
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{
                r: 6,
              }}
              connectNulls
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
